/**
 * Firebase application context.
 *
 * The single place the Firebase SDK is initialised. Everything downstream (the
 * auth service now; profile and retrieval services later) receives the already
 * initialised `auth` and `db` handles from here, so no other module calls
 * `initializeApp` or reaches for a global Firebase instance.
 *
 * Emulator wiring lives here too. When `useEmulators` is set the SDK is pointed at
 * the Local Emulator Suite instead of real Google infrastructure; the ports match
 * `firebase.json`. A production build must never set `useEmulators` — that is
 * enforced upstream in `config/environment.js`, which refuses to build a
 * production target with emulators enabled.
 */

import { getApp, getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { REGION } from "../../config/regions.js";

/** Emulator host and ports. Kept in step with `firebase.json`. */
export const EMULATOR_HOST = "127.0.0.1";
export const AUTH_EMULATOR_PORT = 9099;
export const FIRESTORE_EMULATOR_PORT = 8080;
export const FUNCTIONS_EMULATOR_PORT = 5001;

/**
 * @typedef {object} FirebaseContext
 * @property {import("firebase/app").FirebaseApp} app
 * @property {import("firebase/auth").Auth} auth
 * @property {import("firebase/firestore").Firestore} db
 */

/**
 * Initialise (or reuse) a Firebase app and return its auth and Firestore handles.
 *
 * A named app can be created more than once in a process — integration tests do
 * exactly that — so an existing app of the same name is reused rather than
 * re-initialised, and the emulators are connected only on first creation
 * (connecting a second time throws once the services are in use).
 *
 * @param {import("../../config/environment.js").FirebaseConfig} config
 * @param {{ useEmulators?: boolean, name?: string }} [options]
 * @returns {FirebaseContext}
 */
export function createFirebaseContext(config, options = {}) {
  const name = options.name ?? "[DEFAULT]";
  const existing = getApps().some((candidate) => candidate.name === name);

  const app = existing
    ? getApp(name)
    : initializeApp(
        {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          appId: config.appId,
        },
        name === "[DEFAULT]" ? undefined : name,
      );

  const auth = getAuth(app);
  const db = getFirestore(app);

  if (options.useEmulators && !existing) {
    // disableWarnings silences the console banner the SDK prints when talking to
    // the auth emulator; it is expected here, not a misconfiguration.
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${AUTH_EMULATOR_PORT}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, EMULATOR_HOST, FIRESTORE_EMULATOR_PORT);
    // Connected against the same region the profile service asks for; a mismatch
    // surfaces as an opaque 404 rather than a configuration error.
    connectFunctionsEmulator(getFunctions(app, REGION), EMULATOR_HOST, FUNCTIONS_EMULATOR_PORT);
  }

  return { app, auth, db };
}
